import { useEffect } from 'react';
import { htmlToText } from '~/helpers/data';
import { setContentTags } from '~/helpers/tag-manager';
import { camelCaseKeys } from '~/helpers/page-data-utils';
import announcePageTitle from '~/components/shell/announce-page-title';
import { useLocation } from 'react-router-dom';
function setCanonicalPath(newPath) {
    var _a;
    const el = document.createElement('link');
    const titleEl = document.querySelector('head title');
    if (titleEl) {
        el.setAttribute('rel', 'canonical');
        (_a = titleEl.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(el, titleEl.nextSibling);
    }
    const host = 'https://openstax.org';
    el.href = `${host}${newPath}`;
    return el;
}
export function useCanonicalLink(controlsHeader = true, path) {
    const defaultPath = useLocation().pathname;
    const newPath = (path || defaultPath).replace(/\/$/, '');
    useEffect(() => {
        if (!controlsHeader) {
            return () => null;
        }
        const linkController = setCanonicalPath(newPath);
        return () => linkController.remove();
    }, [controlsHeader, newPath]);
}
function getPageDescriptionElement() {
    return document.querySelector('head meta[name="description"]');
}
export function getPageDescription() {
    var _a;
    return (_a = getPageDescriptionElement()) === null || _a === void 0 ? void 0 : _a.getAttribute('content');
}
export function setPageDescription(description) {
    const defaultDescription = 'Access our free college textbooks and low-cost learning materials.';
    const descriptionEl = getPageDescriptionElement();
    if (descriptionEl) {
        descriptionEl.setAttribute('content', description || defaultDescription);
    }
    else {
        console.warn('No description meta entry in page header');
    }
}
function setPageTitleAndDescription(title = 'OpenStax', description) {
    var _a;
    setPageDescription(description);
    document.title = title.includes('OpenStax') ? title : `${title} - OpenStax`;
    (_a = document.getElementById('main')) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
    announcePageTitle(document.title);
}
// eslint-disable-next-line complexity
export function setPageTitleAndDescriptionFromBookData(data = {}) {
    const meta = camelCaseKeys(data.meta);
    const defaultDescription = data.description
        ? htmlToText(data.description)
        : '';
    // setPageTitleAndDescriptionFromBookData is actually called with a variety
    // of page data types, not just books
    const contentTags = 
    // eslint-disable-next-line no-nested-ternary
    /* books */ data.bookSubjects && data.bookCategories
        ? [
            `book=${data.title}`,
            ...data.bookSubjects.map((subject) => `subject=${subject.subjectName}`),
            ...data.bookCategories.map((category) => `category=${category.subjectCategory} (${category.subjectName})`)
        ]
        : /* blog posts */ data.articleSubjects && data.collections
            ? [
                ...data.collections
                    .map((item) => 'name' in item
                    ? item.name
                    : item.value.map(({ collection }) => collection.name))
                    .flat(2)
                    .map((name) => `collection=${name}`),
                ...data.articleSubjects
                    .map((item) => 'name' in item
                    ? item.name
                    : item.value.map(({ subject }) => subject.name))
                    .flat(2)
                    .map((name) => `subject=${name}`)
            ]
            : [];
    setContentTags(contentTags);
    setPageTitleAndDescription((meta === null || meta === void 0 ? void 0 : meta.seoTitle) || data.title, (meta === null || meta === void 0 ? void 0 : meta.searchDescription) || defaultDescription);
}
export default function useDocumentHead({ title, description = undefined, noindex = false }) {
    useEffect(() => {
        setPageTitleAndDescription(title, description !== null && description !== void 0 ? description : undefined);
    }, [title, description]);
    useEffect(() => {
        if (noindex) {
            const tag = document.createElement('meta');
            tag.setAttribute('content', 'noindex');
            tag.setAttribute('name', 'robots');
            document.head.appendChild(tag);
            return () => {
                document.head.removeChild(tag);
            };
        }
        return () => null;
    }, [noindex]);
}
